import { AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ScanCandidateDto, ScanWindow } from 'shared';

import { StockCandidateList as CandidateList } from '../components/domain/StockCandidateList';
import { HorizonSelector } from '../components/ui/HorizonSelector';
import { SymbolExport } from '../components/ui/SymbolExport';
import { useMarketStocks, useMarketUniverse } from '../features/marketConsole/queries';
import { compactDollars, SCAN_HORIZON_OPTIONS } from '../lib/marketConsoleFormat';
import { useReplayDate } from '../lib/useReplayDate';

function byHorizon(
  candidates: readonly ScanCandidateDto[],
  horizon: ScanWindow,
): ScanCandidateDto[] {
  return [...candidates].sort(
    (left, right) =>
      right.performance[horizon] - left.performance[horizon] ||
      left.symbol.localeCompare(right.symbol),
  );
}

function inWindow(
  candidates: readonly ScanCandidateDto[],
  horizon: ScanWindow,
): ScanCandidateDto[] {
  return byHorizon(
    candidates.filter((candidate) => candidate.topWindows.includes(horizon)),
    horizon,
  );
}

const UNIVERSE_PAGE = 50;

function Section({
  id,
  title,
  description,
  actions,
  children,
}: {
  id: string;
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface" aria-labelledby={id}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 id={id} className="text-sm font-semibold">
            {title}
          </h2>
          <p className="mt-0.5 text-xs text-text-muted">{description}</p>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function FilteredUniverse({
  asOf,
  horizon,
  maxAtrExtension,
}: {
  asOf: string | undefined;
  horizon: ScanWindow;
  maxAtrExtension: number;
}) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(UNIVERSE_PAGE);
  const universe = useMarketUniverse(asOf, open);
  const rows = universe.data ? byHorizon(universe.data.stocks, horizon) : [];

  return (
    <Section
      id="stocks-universe"
      title="Filtered universe"
      description="Every stock passing the liquidity, ADR% and industry filters before ranking, by the selected horizon."
      actions={
        open && universe.data?.sessionDate ? (
          <SymbolExport
            label="filtered universe"
            fileName={`filtered-universe-${universe.data.sessionDate}.txt`}
            rows={rows}
          />
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="h-8 rounded border border-border bg-bg px-3 text-xs hover:bg-surface-raised"
          >
            Show list
          </button>
        )
      }
    >
      {!open ? null : universe.isLoading ? (
        <p className="px-4 py-6 text-sm text-text-muted">Loading universe…</p>
      ) : universe.isError || !universe.data ? (
        <p className="px-4 py-6 text-sm text-danger">The filtered universe could not be loaded.</p>
      ) : universe.data.status === 'NOT_AVAILABLE' ? (
        <p className="px-4 py-6 text-sm text-text-muted">
          This scan predates the stored universe. It is kept from the next scan on, for about three
          months.
        </p>
      ) : (
        <>
          <p className="px-4 pt-3 font-mono text-xs tabular-nums text-text-muted">
            {rows.length.toLocaleString('en-US')} stocks · showing {Math.min(shown, rows.length)}
          </p>
          <CandidateList
            candidates={rows.slice(0, shown)}
            horizon={horizon}
            emptyText="No stock passes the filters."
            maxAtrExtension={maxAtrExtension}
          />
          {shown < rows.length ? (
            <div className="border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setShown((value) => value + UNIVERSE_PAGE)}
                className="h-9 rounded border border-border bg-bg px-3 text-sm hover:bg-surface-raised"
              >
                Show {Math.min(UNIVERSE_PAGE, rows.length - shown)} more
              </button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

export function MarketStocks() {
  const { asOf } = useReplayDate();
  const stocks = useMarketStocks(asOf);
  const [horizon, setHorizon] = useState<ScanWindow>('1M');

  if (stocks.isLoading) {
    return <main className="p-6 text-text-muted">Loading stock scan…</main>;
  }
  if (stocks.isError || !stocks.data) {
    return <main className="p-6 text-danger">The stock scan could not be loaded.</main>;
  }

  const { snapshot, source } = stocks.data;
  const leaders = snapshot ? inWindow(snapshot.nonExtendedLeaders, horizon) : [];
  const tight = leaders.filter((candidate) => candidate.coil?.isTight === true);
  const superLiquid = snapshot ? inWindow(snapshot.superLiquid, horizon) : [];
  const momentum = snapshot?.momentumLeaders ? inWindow(snapshot.momentumLeaders, horizon) : [];
  const maxAtrExtension = snapshot?.settings.maxAtrExtension ?? 0;
  const fileName = (list: string) => `${list}-${horizon}-${snapshot?.sessionDate ?? ''}.txt`;

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Stocks</h1>
            <p className="mt-1 text-sm text-text-muted">
              Momentum leaders that are not yet stretched, and the ones coiling tightest.
            </p>
          </div>
          {snapshot ? (
            <div className="flex flex-col gap-1 text-xs text-text-muted sm:items-end">
              <span>Close · {snapshot.sessionDate} · Informational only</span>
              <Link to="/settings/stock-scan" className="inline-flex items-center gap-1 underline">
                <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
                {snapshot.customSettings ? 'Your scan thresholds' : 'Scan thresholds'}
              </Link>
            </div>
          ) : null}
        </header>

        {snapshot && !snapshot.settingsApplied ? (
          <p className="rounded border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn">
            Your scan thresholds could not be applied to this session because its full universe was
            not kept. Showing the default scan.
          </p>
        ) : null}

        {!snapshot ? (
          <section className="flex gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4 text-sm text-warn">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              No stock scan has run yet. Run the stock leaders scan in{' '}
              <Link to="/settings/market-data" className="underline">
                Settings → Market data
              </Link>{' '}
              after the close.
            </p>
          </section>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                Ranking horizon
              </span>
              <HorizonSelector
                label="Ranking horizon"
                options={SCAN_HORIZON_OPTIONS}
                value={horizon}
                onChange={setHorizon}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-mono text-xs tabular-nums text-text-muted">
                Scanned {snapshot.counts.scanned.toLocaleString('en-US')} · eligible{' '}
                {snapshot.counts.eligible.toLocaleString('en-US')} · leaders{' '}
                {snapshot.counts.leaders} · non-extended {snapshot.counts.nonExtended} · tight{' '}
                {snapshot.counts.tight}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted">Non-extended, all horizons</span>
                <SymbolExport
                  label="non-extended leaders, all horizons"
                  fileName={`non-extended-leaders-all-${snapshot.sessionDate}.txt`}
                  rows={snapshot.nonExtendedLeaders}
                />
              </div>
            </div>

            <Section
              id="stocks-tight"
              title={`Tight coil · ${horizon}`}
              description="Non-extended leaders above the 9-day EMA, with a 5-day range within 2.5 ATR, today's range below the prior ATR, and contracting 3- or 5-day closes and ranges."
              actions={
                <SymbolExport
                  label={`tight coil ${horizon}`}
                  fileName={fileName('tight-coil')}
                  rows={tight}
                />
              }
            >
              <CandidateList
                candidates={tight}
                horizon={horizon}
                emptyText="No leader in this horizon is coiling tightly right now."
                maxAtrExtension={maxAtrExtension}
              />
            </Section>

            <Section
              id="stocks-leaders"
              title={`Non-extended leaders · ${horizon}`}
              description={`Top ${Math.round(snapshot.settings.topPct * 100)}% by performance among stocks over ${compactDollars.format(snapshot.settings.minAverageDollarVolume30d)} a day, ADR above ${snapshot.settings.minAdrPct}%${snapshot.settings.excludeBiotech === false ? '' : ' (biotech excluded)'}, no more than ${snapshot.settings.maxAtrExtension} ATRs above the 50-day average.`}
              actions={
                <SymbolExport
                  label={`non-extended leaders ${horizon}`}
                  fileName={fileName('non-extended-leaders')}
                  rows={leaders}
                />
              }
            >
              <CandidateList
                candidates={leaders}
                horizon={horizon}
                emptyText="No non-extended leader in this horizon."
                maxAtrExtension={maxAtrExtension}
              />
            </Section>

            <Section
              id="stocks-super-liquid"
              title={`Super liquid leaders · ${horizon}`}
              description={`Top ${snapshot.settings.superLiquidTopPerWindow} stocks trading over ${compactDollars.format(snapshot.settings.superLiquidMinAverageDollarVolume30d)} a day. Stats are the underlying's; a 2× ETF is shown as an alternate vehicle.`}
              actions={
                <SymbolExport
                  label={`super liquid leaders ${horizon}`}
                  fileName={fileName('super-liquid')}
                  rows={superLiquid}
                />
              }
            >
              <CandidateList
                candidates={superLiquid}
                horizon={horizon}
                emptyText="No super liquid leader in this horizon."
                maxAtrExtension={maxAtrExtension}
              />
            </Section>

            <Section
              id="stocks-momentum"
              title={`Momentum leaders · ${horizon}`}
              description={`Every top-${Math.round(snapshot.settings.topPct * 100)}% stock before the extension filter. Names more than ${snapshot.settings.maxAtrExtension} ATRs over the 50-day are marked ext.`}
              actions={
                snapshot.momentumLeaders ? (
                  <SymbolExport
                    label={`momentum leaders ${horizon}`}
                    fileName={fileName('momentum-leaders')}
                    rows={momentum}
                  />
                ) : null
              }
            >
              {snapshot.momentumLeaders ? (
                <CandidateList
                  candidates={momentum}
                  horizon={horizon}
                  emptyText="No momentum leader in this horizon."
                  maxAtrExtension={maxAtrExtension}
                />
              ) : (
                <p className="px-4 py-6 text-sm text-text-muted">
                  This scan predates the stored universe. Momentum leaders appear from the next scan
                  on.
                </p>
              )}
            </Section>

            <FilteredUniverse asOf={asOf} horizon={horizon} maxAtrExtension={maxAtrExtension} />

            {snapshot.coilUnavailable.length > 0 ? (
              <p className="text-xs text-warn">
                Coil check unavailable (no current daily bars, or outside the checked leaders):{' '}
                {snapshot.coilUnavailable.join(', ')}.
              </p>
            ) : null}
          </>
        )}

        <p className="text-xs text-text-muted">
          Data:{' '}
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline">
            {source.name}
          </a>
          . A shortlist for chart review, not a buy list: check structure, resistance, earnings and
          your trade plan.
        </p>
      </div>
    </main>
  );
}
